import cx from 'classnames';
import { isFunction } from 'lodash';
import { StepperContext, useStepperContext } from 'components/Stepper/context';
import { StepProps, StepperProps } from 'components/Stepper/types';
import { Icon } from 'components/Icon';
import { Heading } from 'components/Typography';

const Step: React.FC<StepProps> = ({ index, isCompleted, className, children }) => {
  const { currentStep, onNavigate } = useStepperContext();

  // @note(juan): better define rules for navigating to incomplete steps and jumping ahead, define variations as a list
  const isClickable = isFunction(onNavigate) && currentStep !== index && isCompleted;
  const isCurrentStep = currentStep === index;

  const isDisabled = !isCompleted || isCurrentStep;

  return (
    <li
      className={cx(
        'flex grow items-center',
        `after:block after:content-[''] after:h-[1px] after:mx-8 after:bg-black-200 after:grow after:w-10`,
        'after:last:hidden',
        'last:grow-0',
        {
          'border-indigo-700': index <= currentStep,
          'hover:border-indigo-300 cursor-pointer': isClickable,
        },
        className,
      )}
    >
      <button
        onClick={() => isClickable && onNavigate?.(index)}
        className="flex items-center gap-8 group"
        disabled={isDisabled}
      >
        {(!isCompleted || isCurrentStep) && (
          <div
            className={cx(
              'flex shrink-0 items-center justify-center h-32 w-32 rounded-full',
              'border border-black-900',
              'text-lh8 font-semibold',
              {
                'bg-black-900 text-white': isCurrentStep,
                'text-black-900': !isCurrentStep,
              },
            )}
          >
            {index + 1}
          </div>
        )}
        {isCompleted && !isCurrentStep && (
          <div
            className={cx(
              'flex shrink-0 items-center justify-center bg-orange-300 rounded-full h-32 w-32',
              'group-hover:bg-orange-400',
            )}
          >
            <Icon name="CheckSlim" className="w-16 h-12" />
          </div>
        )}
        <Heading
          level="h7"
          className={cx('font-semibold lg-d:hidden', {
            'group-hover:underline': isClickable,
          })}
        >
          {children}
        </Heading>
      </button>
    </li>
  );
};

type StepperComposition = {
  Step: typeof Step;
};

const Stepper: React.FC<StepperProps> & StepperComposition = ({
  currentStep,
  className,
  onNavigate,
  children,
  ...rest
}) => {
  return (
    <ol {...rest} className={cx(`flex justify-between`, className)}>
      <StepperContext.Provider
        value={{
          currentStep,
          onNavigate,
        }}
      >
        {children}
      </StepperContext.Provider>
    </ol>
  );
};

Stepper.Step = Step;

export default Stepper;
